import merge from "lodash/merge"
import snakeCase from "lodash/snakeCase"
import transform from "lodash/transform"

import constants from "~/constants"
import { useUserStore } from "~/stores/userStore"

import storage from "./storage"

interface IHttpGet extends IErrorAndSuccessCallbacks {
  url: string
  params?: Record<string, any>
  headers?: Record<string, string>
  extras?: Record<string, any>
}

interface IHTTPPost extends IErrorAndSuccessCallbacks {
  url: string
  body?: Record<string, any>
  multipart?: boolean
  headers?: Record<string, string>
  extras?: Record<string, any>
}

interface IHTTPRequest extends IErrorAndSuccessCallbacks {
  method: "get" | "post" | "put" | "delete" | "patch"
  url: string
  params?: Record<string, any>
  body?: Record<string, any>
  multipart?: boolean
  headers?: Record<string, any>
  extras?: Record<string, any>
}

const transformParams = (params: Record<string, any> | undefined): Record<string, any> | undefined => {
  if (!params) {
    return undefined
  }

  return transform(params, paramKeyTransform, {})
}

const paramKeyTransform = (result: Record<string, any>, value: any, key: string) => {
  let transformedKey = key.toString()
  transformedKey.split(/[[\]]+/).forEach((word) => {
    transformedKey = transformedKey.replace(word, snakeCase(word))
  })
  return ((result as any)[transformedKey] = value)
}

export default {
  request({
    method,
    url,
    params,
    body,
    multipart,
    successCallback = null,
    errorCallback = null,
    headers = {},
    extras = { skipUnauthorizedRedirect: false },
  }: IHTTPRequest) {
    const token = storage.get(constants.AUTH_TOKEN_KEY)

    const fullHeaders = merge(headers, {
      "X-Auth-Token": token,
      "Frontend-Version": "V2",
      ...(!multipart && { "Content-Type": "application/json" })
    })

    const transformedParams = transformParams(params)

    return $fetch(url, {
      method,
      body,
      params: transformedParams,
      headers: fullHeaders,
      baseURL: `${useRuntimeConfig().public.apiBase}/api`,
      onResponse: (context) => {
        window.userOnline = true
        !context.error &&
          context.response &&
          context.response.status < 400 &&
          successCallback &&
          successCallback(context)
      },
      onResponseError: (context) => {
        if (context.response.status === 401 && !extras.skipUnauthorizedRedirect) {
          useUserStore().logout()
        } else if (context.response.status === 403) {
          const { response } = context
          if (response._data?.code === "bad_plan") {
            useUserStore().upgradeNoticeWithRedirectHome = true
          } else {
            errorCallback && errorCallback(response)
          }
        } else {
          const { response } = context
          errorCallback &&
            errorCallback({ message: response._data?.error, code: response._data?.code, status: response.status })
        }
      },
      onRequestError: (context) => {
        window.userOnline = false
        errorCallback && errorCallback({ message: context.error.message, status: -1 })
      },
    })
  },

  get({ url, params = {}, headers, successCallback = null, errorCallback = null, extras }: IHttpGet) {
    return this.request({
      method: "get",
      url,
      params,
      successCallback,
      errorCallback,
      headers,
      extras,
    })
  },

  post({ url, body, multipart, headers, successCallback = null, errorCallback = null, extras }: IHTTPPost) {
    return this.request({
      method: "post",
      url,
      body,
      multipart,
      successCallback,
      errorCallback,
      headers,
      extras,
    })
  },

  put({ url, body, multipart, headers, successCallback = null, errorCallback = null, extras }: IHTTPPost) {
    return this.request({
      method: "put",
      url,
      body,
      multipart,
      successCallback,
      errorCallback,
      headers,
      extras,
    })
  },

  patch({ url, body, multipart, headers, successCallback = null, errorCallback = null, extras }: IHTTPPost) {
    return this.request({
      method: "patch",
      url,
      body,
      multipart,
      successCallback,
      errorCallback,
      headers,
      extras,
    })
  },

  delete({ url, body, multipart, headers, successCallback = null, errorCallback = null, extras }: IHTTPPost) {
    return this.request({
      method: "delete",
      url,
      body,
      multipart,
      successCallback,
      errorCallback,
      headers,
      extras,
    })
  },
}
